/** @jsxRuntime classic */
/** @jsx jsx */
import { ClassNames, css, jsx } from "@emotion/react";
import IntlTelInput, { intlTelInput } from "@intl-tel-input/react/with-utils";
import ErrorIcon from "@material-ui/icons/Error";
import { useI18n } from "hooks/i18n/i18n";
import { useId, useRef } from "react";

import { DEFAULT_COUNTRY } from "./formatPhoneNumber";

import "intl-tel-input/styles";

import type { IntlTelInputRef } from "@intl-tel-input/react/with-utils";
import type { FC } from "react";

/**
 * portal は zh_cn のようにアンダースコア区切りを使うが、Intl は BCP 47 しか
 * 受け付けない。解釈できない値を渡すと intl-tel-input は国名を空文字にするため、
 * その場合は英語の国名にフォールバックする。
 */
const toCountryNameLocale = (language: string): string => {
  try {
    return Intl.getCanonicalLocales(language.replace("_", "-"))[0] ?? "en";
  } catch (error) {
    console.error(error);
    return "en";
  }
};

/**
 * 狭い画面では国選択が全画面表示になり、ライブラリが要素ごと body 直下へ移す。
 * 入力欄を包む要素の下に書いた指定は届かないため、要素そのものに当てる。
 *
 * ライブラリはこの要素に font を指定せず、フォームコントロールは font を
 * 継承しないため UA 既定の 13.3px になる。iOS は 16px 未満の入力にフォーカス
 * すると画面を拡大するが、国選択を開くとライブラリがここへフォーカスを移す。
 */
const searchInputStyle = css({
  fontFamily: "inherit",
  fontSize: "16px",
});

type Props = {
  label: string;
  onChangeNumber: (number: string) => void;
  onChangeValidity: (isValid: boolean) => void;
  onBlur: () => void;
  showError: boolean;
  disabled: boolean;
};

/**
 * 固定電話・IP電話は allowedNumberTypes で弾くが、北米のように番号計画上
 * 携帯と固定を区別しない国の固定電話は弾けない。
 */
export const PhoneNumberField: FC<Props> = ({
  label,
  onChangeNumber,
  onChangeValidity,
  onBlur,
  showError,
  disabled,
}) => {
  const { t, i18n } = useI18n();
  const intlTelInputRef = useRef<IntlTelInputRef>(null);
  const inputId = useId();
  const descriptionId = useId();
  const classes = styles();

  return (
    <div css={classes.container}>
      <label css={classes.label} htmlFor={inputId}>
        {label}
      </label>
      <div css={[classes.fields, showError && classes.fieldsError]}>
        <ClassNames>
          {({ css: toClassName }) => (
            <IntlTelInput
              ref={intlTelInputRef}
              searchInputClass={toClassName(searchInputStyle)}
              initialCountry={DEFAULT_COUNTRY}
              countryOrder={[DEFAULT_COUNTRY]}
              countryNameLocale={toCountryNameLocale(i18n.language)}
              separateDialCode
              allowedNumberTypes={["MOBILE"]}
              // 既定の判定は桁数が携帯として有り得るかしか見ず、050 のIP電話や
              // 090-0000-0000 のような欠番も通る。番号の一意性を複垢対策に使うため、
              // 番号計画と照合する厳密な判定にする
              usePreciseValidation
              // 入力した通りに見せる。整形させると国内プレフィックスの 0 を
              // 付けたときだけ整形が効かず、付けないときだけ効いて挙動が揃わない
              formatAsYouType={false}
              // 既定の例は区切り付きで国内プレフィックスも落ちた 90-1234-5678 になる。
              // 入力してほしい形と揃えて、数字だけの 09012345678 を見せる
              customPlaceholder={(placeholder, country) => {
                const digits = placeholder.replace(/\D/g, "");
                return country?.nationalPrefix == null
                  ? digits
                  : `${country.nationalPrefix}${digits}`;
              }}
              disabled={disabled}
              onChangeNumber={onChangeNumber}
              onChangeValidity={onChangeValidity}
              inputProps={{
                id: inputId,
                "aria-invalid": showError,
                "aria-describedby": descriptionId,
                inputMode: "tel",
                autoComplete: "tel-national",
                onBlur,
              }}
            />
          )}
        </ClassNames>
      </div>
      {showError ? (
        <p css={classes.error} id={descriptionId} role="alert">
          <ErrorIcon css={classes.errorIcon} />
          <span>{t(toErrorMessageKey(intlTelInputRef.current))}</span>
        </p>
      ) : (
        <p css={classes.hint} id={descriptionId}>
          {t("phoneNumber.inputHint")}
        </p>
      )}
    </div>
  );
};

/**
 * onChangeErrorCode は値が変わったときにしか呼ばれず、空欄の時点で既に
 * TOO_SHORT が入っているため、途中まで入力しても発火しない。
 * 表示する時点でインスタンスから読む。
 */
const toErrorMessageKey = (intlTelInputRef: IntlTelInputRef | null) => {
  const instance = intlTelInputRef?.getInstance();
  if (instance === null || instance === undefined) {
    return "phoneNumber.errorInvalidPhoneNumber" as const;
  }
  if (instance.getNumber() === "") {
    return "phoneNumber.errorRequired" as const;
  }
  const { VALIDATION_ERROR } = intlTelInput;
  switch (instance.getValidationError()) {
    case VALIDATION_ERROR.TOO_SHORT:
      return "phoneNumber.errorTooShort" as const;
    case VALIDATION_ERROR.TOO_LONG:
      return "phoneNumber.errorTooLong" as const;
    case VALIDATION_ERROR.INVALID_COUNTRY_CODE:
      return "phoneNumber.errorInvalidCountryCode" as const;
    default:
      return "phoneNumber.errorInvalidPhoneNumber" as const;
  }
};

const styles = () => {
  return {
    container: css({
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    }),
    label: css({
      margin: 0,
      fontSize: "15px",
      lineHeight: "20px",
      fontWeight: 500,
    }),
    /**
     * 国選択は既定では入力欄の内側に重ねて絶対配置される。国番号と番号は別の
     * 入力なので、それぞれ独立した枠として横に並べ直す。
     */
    fields: css({
      ".iti": {
        display: "flex",
        gap: "8px",
        width: "100%",
      },
      ".iti__country-container": {
        // 既定の absolute を外して並びに戻す。relative はドロップダウンの位置の基準
        position: "relative",
        flex: "none",
        width: "124px",
        height: "44px",
        padding: 0,
        border: "1px solid #d2d2d2",
        backgroundColor: "#fff",
        "@media screen and (min-width: 768px)": {
          width: "150px",
          height: "40px",
        },
      },
      ".iti__selected-country": {
        width: "100%",
        height: "100%",
        paddingLeft: "12px",
      },
      // 旗と矢印を包む箱を畳んで、旗・国名・国番号・矢印を1列に並べる
      ".iti__selected-country-primary": {
        display: "contents",
      },
      ".iti__selected-dial-code": {
        order: 1,
        marginLeft: "6px",
        fontSize: "16px",
        lineHeight: "24px",
      },
      // 三角形を border で描く既定を捨てて、デザイン通りの箱と矢印にする
      ".iti__arrow": {
        order: 2,
        position: "relative",
        width: "30px",
        height: "100%",
        margin: "0 0 0 auto",
        border: "none",
        transform: "none",
        backgroundColor: "#e0e0e0",
        // 矢印はライブラリが作る要素なのでコンポーネントを置けない。
        // 既定は border で描いた斜め線なので、塗りつぶしの三角に描き直す
        "&::after": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 0,
          height: 0,
          margin: "-3px 0 0 -5px",
          borderRight: "5px solid transparent",
          borderLeft: "5px solid transparent",
          borderTop: "6px solid #6f6f6f",
        },
      },
      ".iti__arrow--up": {
        margin: "0 0 0 auto",
        "&::after": {
          transform: "rotate(180deg)",
        },
      },
      ".iti input.iti__tel-input": {
        flex: 1,
        // 既定の min-width:100% は flex の中では入力欄が枠からはみ出す
        minWidth: 0,
        height: "44px",
        padding: "0 12px",
        // 国選択を入力欄に重ねる作りのため、ライブラリがその幅ぶんの
        // padding-left を style 属性で直に入れてくる。重ねるのをやめた今は
        // 文字が中途半端な位置から始まるだけなので打ち消す
        paddingLeft: "12px !important",
        fontSize: "16px",
        lineHeight: "24px",
        border: "1px solid #d2d2d2",
        borderRadius: 0,
        "&::placeholder": {
          color: "#9e9e9e",
        },
        "@media screen and (min-width: 768px)": {
          height: "40px",
        },
      },
    }),
    fieldsError: css({
      ".iti input.iti__tel-input": {
        border: "2px solid #e53935",
      },
    }),
    hint: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    error: css({
      display: "flex",
      margin: 0,
      gap: "6px",
      alignItems: "flex-start",
      color: "#e53935",
      fontSize: "13px",
      fontWeight: "bold",
      lineHeight: "20px",
    }),
    errorIcon: css({
      flex: "none",
      fontSize: "16px",
      marginTop: "2px",
    }),
  };
};
